import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import './FuroManual.css';
import { EIXO_TRAVADO, EIXO_LIVRE, valorBordaTravada, coordenadasHorizontais } from './furoHorizontalLado';

// OPÇÃO A (decisão de produto, Enzo): para furo horizontal, o `lado` MANDA.
// A coordenada PERPENDICULAR à borda de entrada fica TRAVADA no valor da borda
// (derivada do lado + dimensões); o usuário só informa a coordenada AO LONGO da
// borda (+ z/profundidade/diâmetro). Garante coerência lado×geometria por
// construção — o invariante que o renderer (Canvas3D via extensaoBrocaHorizontal)
// assume ao derivar a borda da GEOMETRIA. Regra e tabela de eixos em
// ./furoHorizontalLado.js; contexto em docs/dividas/registro.md "furo-manual-lado".

function FuroManual({ onAddFuro, pecaDimensoes }) {
  const [furoData, setFuroData] = useState({
    tipo: 'vertical',
    x: '',
    y: '',
    z: '',
    diametro: '5',
    profundidade: '0',
    lado: 'XP',
    ladoVertical: 'LS'
  });

  const [errors, setErrors] = useState({});

  const handleChange = (campo, valor) => {
    setFuroData({
      ...furoData,
      [campo]: valor
    });

    // Limpar erro do campo quando usuário editar
    if (errors[campo]) {
      setErrors({
        ...errors,
        [campo]: null
      });
    }
  };

  // Troca de lado (só horizontal): a coord travada recalcula sozinha (é
  // derivada no render/submit). Se a FAMÍLIA de borda muda (X-edge XP/XM <->
  // Y-edge YP/YM), o eixo livre passa de y para x (ou vice-versa) — limpa
  // ambos para o usuário digitar a nova coord livre sem herdar valor de borda.
  const handleLadoChange = (novoLado) => {
    setFuroData((prev) => {
      if (EIXO_LIVRE[prev.lado] === EIXO_LIVRE[novoLado]) {
        return { ...prev, lado: novoLado };
      }
      return { ...prev, lado: novoLado, x: '', y: '' };
    });
    if (errors.x || errors.y) {
      setErrors((prev) => ({ ...prev, x: null, y: null }));
    }
  };

  const validarFuro = () => {
    const novosErros = {};

    if (furoData.tipo === 'horizontal') {
      // Só a coordenada LIVRE é validada; a travada está sobre a borda por
      // construção (coerente com o lado).
      const eixoLivre = EIXO_LIVRE[furoData.lado];
      const max = eixoLivre === 'x' ? pecaDimensoes.comprimento : pecaDimensoes.largura;
      const livre = parseFloat(furoData[eixoLivre]);
      if (isNaN(livre) || livre < 0 || livre > max) {
        novosErros[eixoLivre] = `${eixoLivre.toUpperCase()} deve estar entre 0 e ${max}mm`;
      }
    } else {
      // Vertical: X e Y são ambos livres.
      const x = parseFloat(furoData.x);
      if (isNaN(x) || x < 0 || x > pecaDimensoes.comprimento) {
        novosErros.x = `X deve estar entre 0 e ${pecaDimensoes.comprimento}mm`;
      }
      const y = parseFloat(furoData.y);
      if (isNaN(y) || y < 0 || y > pecaDimensoes.largura) {
        novosErros.y = `Y deve estar entre 0 e ${pecaDimensoes.largura}mm`;
      }
    }

    // Validar Diâmetro
    const diametro = parseFloat(furoData.diametro);
    if (isNaN(diametro) || diametro <= 0 || diametro > 50) {
      novosErros.diametro = 'Diâmetro deve estar entre 0.1 e 50mm';
    }

    // Validar Profundidade
    const profundidade = parseFloat(furoData.profundidade);
    if (isNaN(profundidade) || profundidade < 0) {
      novosErros.profundidade = 'Profundidade não pode ser negativa';
    }

    setErrors(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validarFuro()) {
      return;
    }

    let xVal;
    let yVal;
    if (furoData.tipo === 'horizontal') {
      // Coord travada derivada do lado; coord livre digitada pelo usuário.
      const livre = furoData[EIXO_LIVRE[furoData.lado]];
      ({ x: xVal, y: yVal } = coordenadasHorizontais(furoData.lado, pecaDimensoes, livre));
    } else {
      xVal = parseFloat(furoData.x);
      yVal = parseFloat(furoData.y);
    }

    const novoFuro = {
      x: xVal,
      y: yVal,
      z: furoData.tipo === 'horizontal' ? (parseFloat(furoData.z) || pecaDimensoes.espessura / 2) : null,
      tipo: furoData.tipo,
      diametro: parseFloat(furoData.diametro),
      profundidade: parseFloat(furoData.profundidade),
      lado: furoData.tipo === 'horizontal' ? furoData.lado : furoData.ladoVertical,
      uid: uuidv4().replace(/-/g, ''),
    };

    onAddFuro(novoFuro);

    // Resetar formulário (mantém tipo, diâmetro e lado). Para horizontal, x/y
    // limpos — a coord travada volta a derivar do lado no próximo render.
    setFuroData({
      ...furoData,
      x: '',
      y: '',
      profundidade: furoData.tipo === 'vertical' ? '0' : '11.5'
    });
  };

  // Estado derivado para a renderização dos inputs X/Y quando horizontal.
  const ehHorizontal = furoData.tipo === 'horizontal';
  const eixoTravado = ehHorizontal ? EIXO_TRAVADO[furoData.lado] : null;
  const bordaTravada = ehHorizontal ? valorBordaTravada(furoData.lado, pecaDimensoes) : null;
  const xTravado = eixoTravado === 'x';
  const yTravado = eixoTravado === 'y';

  return (
    <div className="furo-manual">
      <h3>Adicionar por Coordenadas</h3>

      <form onSubmit={handleSubmit} className="furo-manual-form">
      {/* Tipo */}
      <div className="form-group" style={{ maxWidth: 200 }}>
        <select
          value={furoData.tipo}
          onChange={(e) => {
            const novoTipo = e.target.value;
            // Ao virar horizontal, x/y são limpos: a coord travada passa a
            // derivar do lado e a coord livre fica pronta para o usuário digitar.
            setFuroData({
              ...furoData,
              tipo: novoTipo,
              x: novoTipo === 'horizontal' ? '' : furoData.x,
              y: novoTipo === 'horizontal' ? '' : furoData.y,
              z: novoTipo === 'horizontal' ? String(pecaDimensoes.espessura / 2) : '',
              profundidade: novoTipo === 'vertical' ? '0' : '22'
            });
          }}
        >
          <option value="vertical">Vertical</option>
          <option value="horizontal">Horizontal</option>
        </select>
      </div>

      {/* X / Y / Z */}
      <div className="form-row-3">
        <div className="form-group">
          <label>X{xTravado ? ' 🔒' : ''}</label>
          <input
            type="number"
            step="0.1"
            value={xTravado ? bordaTravada : furoData.x}
            onChange={(e) => handleChange('x', e.target.value)}
            placeholder="0"
            readOnly={xTravado}
            title={xTravado ? 'Travado pela borda do lado escolhido' : undefined}
            style={xTravado ? { background: 'rgba(255,255,255,0.06)', opacity: 0.7, cursor: 'not-allowed' } : undefined}
          />
        </div>
        <div className="form-group">
          <label>Y{yTravado ? ' 🔒' : ''}</label>
          <input
            type="number"
            step="0.1"
            value={yTravado ? bordaTravada : furoData.y}
            onChange={(e) => handleChange('y', e.target.value)}
            placeholder="0"
            readOnly={yTravado}
            title={yTravado ? 'Travado pela borda do lado escolhido' : undefined}
            style={yTravado ? { background: 'rgba(255,255,255,0.06)', opacity: 0.7, cursor: 'not-allowed' } : undefined}
          />
        </div>
        {ehHorizontal && (
          <div className="form-group">
            <label>Z</label>
            <input
              type="number"
              step="0.1"
              value={furoData.z}
              onChange={(e) => handleChange('z', e.target.value)}
              placeholder={String(pecaDimensoes.espessura / 2)}
            />
          </div>
        )}
      </div>

      {/* Diâmetro / Profundidade / Lado / Botão */}
      {furoData.tipo === 'vertical' ? (
        <div className="form-row-4">
          <div className="form-group">
            <label>Ø</label>
            <input
              type="number"
              step="0.1"
              value={furoData.diametro}
              onChange={(e) => handleChange('diametro', e.target.value)}
              placeholder="5"
            />
          </div>
          <div className="form-group">
            <label>Prof.</label>
            <input
              type="number"
              step="0.1"
              value={furoData.profundidade}
              onChange={(e) => handleChange('profundidade', e.target.value)}
              placeholder="0"
            />
          </div>
          <div className="form-group">
            <label>Lado</label>
            <select
              value={furoData.ladoVertical}
              onChange={(e) => handleChange('ladoVertical', e.target.value)}
            >
              <option value="LS">↓ De cima</option>
              <option value="LSU">↑ De baixo</option>
            </select>
          </div>
          <div className="form-group">
            <label className="label-hidden">·</label>
            <button type="submit" className="btn-add-furo">Adicionar</button>
          </div>
        </div>
      ) : (
        <div className="form-row-horiz">
          <div className="form-group">
            <label>Ø</label>
            <input
              type="number"
              step="0.1"
              value={furoData.diametro}
              onChange={(e) => handleChange('diametro', e.target.value)}
              placeholder="5"
            />
          </div>
          <div className="form-group">
            <label>Prof.</label>
            <input
              type="number"
              step="0.1"
              value={furoData.profundidade}
              onChange={(e) => handleChange('profundidade', e.target.value)}
              placeholder="0"
            />
          </div>
          {/* Regra oficial [CNC] (bancada WoodWOP 6.0.18, 2026-07-24): a borda de
              ENTRADA da broca define o BM -- X=0 -> XP, X=comprimento -> XM,
              Y=0 -> YP, Y=largura -> YM. Mesma convenção do parser STEP
              (step_parser_occ.py:451-529) e de BORDA_PARA_BM_OFICIAL
              (utils/furoTransforms.js:100). Ver docs/referencia/formato-mpr/campo-bm-horizontal.md §9.1.
              As setas indicam o sentido de avanço da broca, e o botão fica na borda
              por onde ela entra -- no canvas o X cresce para a direita e o Y para baixo.
              Escolher o lado TRAVA a coordenada perpendicular (X ou Y) no valor da
              borda — ver bloco de topo deste arquivo e docs/dividas/registro.md "furo-manual-lado". */}
          <div className="lado-selector">
            <button type="button" title="XP — entra pela borda esquerda (X=0)" className={`lado-btn ${furoData.lado === 'XP' ? 'active' : ''}`} onClick={() => handleLadoChange('XP')}>→</button>
            <div className="lado-meio">
              <button type="button" title="YP — entra pela borda superior (Y=0)" className={`lado-btn ${furoData.lado === 'YP' ? 'active' : ''}`} onClick={() => handleLadoChange('YP')}>↓</button>
              <div className="lado-peca">▭</div>
              <button type="button" title="YM — entra pela borda inferior (Y=largura)" className={`lado-btn ${furoData.lado === 'YM' ? 'active' : ''}`} onClick={() => handleLadoChange('YM')}>↑</button>
            </div>
            <button type="button" title="XM — entra pela borda direita (X=comprimento)" className={`lado-btn ${furoData.lado === 'XM' ? 'active' : ''}`} onClick={() => handleLadoChange('XM')}>←</button>
          </div>
          <button type="submit" className="btn-add-furo">Adicionar</button>
        </div>
      )}
    </form>
    </div>
  );
}

export default FuroManual;
